import React, { useEffect, useRef, useState } from 'react';

import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Col, Panel, Row, Table, NavLinkButton, Button } from '@shared/components/bootstrap';
import { Pagination } from '@shared/components/pagination';
import { useDebounce } from '@shared/hooks';
import { useNumericURLSearchParam } from '@shared/hooks/use_numeric_url_search_param';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';

import { CreateModal } from '@admin/components/accounts/containers/create_modal';
import { InputFormGroup, SelectFormGroup } from '@admin/components/fields';
import { Spinner } from '@admin/components/spinner';
import {
  ACCOUNT_STATES,
  ACCOUNT_STATE_TO_NAME,
  AccountStateParam,
  ACCOUNT_STATE_FROM_PARAM,
  ACCOUNT_STATE_TO_PARAM,
} from '@admin/constants/account_state';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { usePaginatedAccountsQuery, Account__State } from '@admin/schema';

import { State } from './state';

const DEFAULT_PAGE = 1;

const QueryFormGroup: React.FC<{
  query?: string;
  onChange(query?: string): void;
}> = ({ query, onChange }) => (
  <InputFormGroup
    label="Search:"
    type="text"
    value={query ?? ''}
    onChange={(event) => onChange(event.target.value || undefined)}
    placeholder="Search by name, email, or phone"
  />
);

const StateFormGroup: React.FC<{
  state?: Account__State;
  onChange(state?: Account__State): void;
}> = ({ state, onChange }) => (
  <SelectFormGroup
    label="State:"
    onChange={(event) => onChange((event.target.value as Account__State) || undefined)}
    value={state ?? ''}
  >
    <option value=""> - State - </option>
    {ACCOUNT_STATES.map((value, index) => (
      <option value={value} key={index}>
        {ACCOUNT_STATE_TO_NAME[value]}
      </option>
    ))}
  </SelectFormGroup>
);

export const AccountsTable: React.FC = () => {
  useTitle('Accounts');
  const [page, setPage] = useNumericURLSearchParam('page', DEFAULT_PAGE);
  const [state, setState] = useURLSearchParam<AccountStateParam>('state');
  const [query, setQuery] = useURLSearchParam<string>('query');
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  // Backend: AccountListPolicy#read? via paginatedAccounts (ENG-740).
  // Also used in referrals/account_form_control, items_explorer/filters, phone_calls/query.
  const { data, loading } = usePaginatedAccountsQuery({
    client,
    variables: {
      page,
      state: state ? ACCOUNT_STATE_FROM_PARAM[state] : undefined,
      query: useDebounce(query),
    },
  });

  return (
    <>
      <Panel>
        <Panel.Body>
          <Row>
            <Col sm={8}>
              <QueryFormGroup query={query} onChange={setQuery} />
            </Col>
            <Col sm={4}>
              <StateFormGroup
                state={state ? ACCOUNT_STATE_FROM_PARAM[state] : undefined}
                onChange={(state) => setState(state ? ACCOUNT_STATE_TO_PARAM[state] : undefined)}
              />
            </Col>
          </Row>
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Body>
          <Table responsive striped>
            <thead>
              <tr>
                <th className="col-sm-4 text-left">Account #</th>
                <th className="col-zero" />
                <th className="col-sm-4 text-left">Name</th>
                <th className="col-sm-4 text-left">Status</th>
                <th className="col-zero" />
              </tr>
            </thead>
            <tbody>
              {data?.paginated.results.map((account) => (
                <tr key={account.id}>
                  <td className="col-sm-4 text-left">{account.number}</td>
                  <td className="col-zero">{account.customers.length > 1 && <FontAwesomeIcon icon={faUsers} />}</td>
                  <td className="col-sm-4 text-left">{account.customer.name}</td>
                  <td className="col-sm-4 text-left">
                    <State account={account} />
                  </td>
                  <td className="col-zero">
                    <NavLinkButton kind="primary" to={`/accounts/${account.id}`}>
                      View
                    </NavLinkButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {loading && <Spinner />}
          {data && <Pagination pagination={data.paginated.pagination} onPage={setPage} />}
        </Panel.Body>
        <Panel.Footer align="right">
          <Button kind="primary" onClick={() => setShowCreateAccountModal(true)}>
            Create Containers Account
          </Button>
        </Panel.Footer>
      </Panel>
      {showCreateAccountModal && <CreateModal onClose={() => setShowCreateAccountModal(false)} />}
    </>
  );
};
